import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Download, Settings, Menu, X, Gift, Wallet } from "lucide-react";
import { useState } from "react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/udyami-mitra-logo.png" 
              alt="उद्यमी मित्र - System of Success" 
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-gray-600 hover:text-orange-500 font-medium transition-colors"
              data-testid="nav-projects"
            >
              प्रोजेक्ट्स
            </Link>
            <Link
              to="/suppliers"
              className="text-gray-600 hover:text-orange-500 font-medium transition-colors"
              data-testid="nav-suppliers"
            >
              सप्लायर्स
            </Link>
            <Link
              to="/request-project"
              className="text-gray-600 hover:text-orange-500 font-medium transition-colors"
              data-testid="nav-request"
            >
              नया प्रोजेक्ट
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-gray-700 hover:text-orange-500"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="hidden lg:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border-gray-200 shadow-lg">
                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer hover:bg-gray-50"
                    data-testid="menu-profile"
                  >
                    <Gift className="w-4 h-4 mr-2 text-orange-500" />
                    रेफर करें ₹50 कमाएं
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/downloads")}
                    className="cursor-pointer hover:bg-gray-50"
                    data-testid="menu-downloads"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    मेरे डाउनलोड्स
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem
                      onClick={() => navigate("/admin")}
                      className="cursor-pointer hover:bg-gray-50"
                      data-testid="menu-admin"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer hover:bg-red-50 text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    लॉगआउट
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate("/auth")}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                data-testid="login-btn"
              >
                लॉगिन / रजिस्टर
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-orange-500 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                प्रोजेक्ट्स
              </Link>
              <Link
                to="/suppliers"
                className="text-gray-700 hover:text-orange-500 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                सप्लायर्स
              </Link>
              <Link
                to="/request-project"
                className="text-gray-700 hover:text-orange-500 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                नया प्रोजेक्ट बनाएं
              </Link>
              {user ? (
                <>
                  <Link
                    to="/downloads"
                    className="text-gray-700 hover:text-orange-500 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    मेरे डाउनलोड्स
                  </Link>
                  <Link
                    to="/profile"
                    className="text-gray-700 hover:text-orange-500 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    रेफर करें ₹50 कमाएं
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      className="text-gray-700 hover:text-orange-500 py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-left text-red-600 py-2"
                  >
                    लॉगआउट
                  </button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    navigate("/auth");
                    setMobileMenuOpen(false);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                >
                  लॉगिन / रजिस्टर
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
